import React, { useEffect, useState } from 'react';

import AutoEmployeeAssignmentDialog from 'pages/tasks/taskEmployeeDistribution/AutoEmployeeAssignmentDialog';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';

import { TaskService } from 'components/services/TaskService';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

interface AutoAssignmentInfoDialogProps {
  visible: boolean;
  onHide: () => void;
  taskId: number;
}

const copyToClipboard = async (text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand('copy');
  document.body.removeChild(textArea);
};

export const AutoAssignmentInfoDialog: React.FC<
  AutoAssignmentInfoDialogProps
> = ({ visible, onHide, taskId }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [openMap, setOpenMap] = useState(false);
  const [tasksToAssignResponse, setTasksToAssignResponse] = useState<any>(null);

  const toast = React.useRef<Toast>(null);

  const fetchAutoAssignmentInfo = async () => {
    if (!taskId) return;

    setLoading(true);
    try {
      const response = await TaskService.getAutoAssignmentInfo(taskId);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching auto assignment info:', error);
    } finally {
      setLoading(false);
    }
  };

  const idBody = (rowData: any) => {
    const idText = String(rowData.taskId ?? '');
    const taskIdValue = rowData.taskId;
    const isCurrentTask = taskIdValue === taskId;

    return (
      <div className="flex align-items-center gap-2">
        <a
          href={`/${taskIdValue}`}
          target="_blank"
          rel="noreferrer"
          style={{
            userSelect: 'text',
            fontWeight: isCurrentTask ? 'bold' : 'normal'
          }}
        >
          {taskIdValue}
        </a>
        <i
          className="pi pi-copy cursor-pointer text-500"
          title="Скопировать номер"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!idText) return;
            copyToClipboard(idText).catch((error) => {
              console.error('Failed to copy task id', error);
            });
          }}
        />
      </div>
    );
  };

  useEffect(() => {
    if (visible && taskId) {
      fetchAutoAssignmentInfo();
    }
  }, [visible, taskId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const responseData = data?.value || data;
  const tasks = responseData?.tasks || [];

  const handleCopyAllInfo = () => {
    const tasksText = tasks
      .map(
        (t: any, index: number) =>
          `  ${index + 1}. №${t.taskId}${
            t.taskId === taskId ? ' (текущий)' : ''
          }${t.name ? ` - ${t.name}` : ''}`
      )
      .join('\n');

    const text = `
      Идентификатор распределения: ${responseData?.changeset || '—'}
      Дата и время: ${responseData?.date ? formatDate(responseData.date) : '—'}
      Кем распределено: ${responseData?.createdBy?.label || '—'}
      Исполнитель: ${responseData?.employee?.label || '—'}
      Наряды:
      ${tasksText}
      `.trim();

    copyToClipboard(text);
    toast.current?.show({
      severity: 'success',
      summary: 'Скопировано',
      detail: 'Информация скопирована в буфер обмена',
      life: 2000
    });
  };

  const handleOpenMap = () => {
    if (!responseData) return;

    const tasksToAssignResponse = [
      {
        personId: responseData.employee?.id,
        displayName: responseData.employee?.label,
        startLatitude: responseData.startLatitude,
        startLongitude: responseData.startLongitude,
        tasks:
          responseData.tasks?.map((task: any) => ({
            taskId: task.taskId,
            name: task.name || '',
            department: {
              label: '',
              id: 0
            },
            taskType: {
              label: '',
              id: 0
            },
            order: task.index ?? 0,
            latitude: task.latitude,
            longitude: task.longitude
          })) || []
      }
    ];
    setTasksToAssignResponse(tasksToAssignResponse);
    setOpenMap(true);
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Информация о распределении"
        style={{ minWidth: '700px', width: '700px' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex gap-3 justify-content-end">
            <Button
              label="Просмотреть на карте"
              icon="pi pi-map"
              className="p-button-secondary"
              onClick={handleOpenMap}
              style={{ minWidth: 42 }}
            />
            <Button
              label="Копировать всё"
              icon="pi pi-copy"
              className="p-button-secondary"
              onClick={handleCopyAllInfo}
            />
            <Button
              label="Закрыть"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={onHide}
            />
          </div>
        }
      >
        {loading ? (
          <div className="flex justify-content-center align-items-center p-5">
            <ProgressSpinner />
          </div>
        ) : (
          <div className="flex flex-column gap-3 p-3">
            <div className="flex justify-content-between">
              <span className="font-bold">Идентификатор:</span>
              <span>{responseData?.changeset || '—'}</span>
            </div>
            <div className="flex justify-content-between">
              <span className="font-bold">Дата и время:</span>
              <span>
                {responseData?.date ? formatDate(responseData.date) : '—'}
              </span>
            </div>
            <div className="flex justify-content-between">
              <span className="font-bold">Кем распределено:</span>
              <span>{responseData?.createdBy?.label || '—'}</span>
            </div>
            <div className="flex justify-content-between">
              <span className="font-bold">Исполнитель:</span>
              <span>{responseData?.employee?.label || '—'}</span>
            </div>

            <div className="mt-2">
              <span className="font-bold block mb-2">Наряды:</span>
              <DataTable
                value={tasks}
                size="small"
                className="p-datatable-sm"
                emptyMessage="Нет нарядов"
              >
                <Column
                  field="index"
                  header="№ п/п"
                  body={(rowData: any) => (rowData.index ?? 0) + 1}
                  style={{ width: '80px' }}
                />
                <Column field="taskId" header="Номер наряда" body={idBody} />
                <Column
                  field="name"
                  header="Название"
                  body={(rowData: any) => rowData.name || '—'}
                />
              </DataTable>
            </div>
          </div>
        )}
      </Dialog>
      <AutoEmployeeAssignmentDialog
        visible={openMap}
        setVisible={setOpenMap}
        tasksToAssignResponse={tasksToAssignResponse}
        basicFooter={true}
      />
    </>
  );
};
